<template>
  <InfuseModal :title="t('e_connector.chargingStations.modal.create.title')" @close="emit('close')">
    <form ref="form" id="create-charging-station" @submit.prevent="onSave()">
      <div class="flex max-h-[475px] max-w-[675px] flex-col gap-4 overflow-y-auto px-5">
        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.chargingStation.name')" class="basis-full" size="small">
            <InfuseInput
              size="small"
              v-model="name"
              :maxlength="255"
              :placeholder="t('e_connector.chargingStations.modal.field.name.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.chargingStation.ocppVersion')" class="basis-1/2" size="small">
            <InfuseSelect
              name="ocppVersion"
              size="small"
              v-model="ocppVersion"
              :options="ocppVersionOptions"
              :label="t('e_connector.chargingStations.modal.field.ocppVersion.placeholder')"
              :required="true"
            />
          </InfuseField>

          <InfuseField :label="t('e_connector.common.chargingStation.type')" class="basis-1/2" size="small">
            <InfuseSelect
              name="type"
              size="small"
              v-model="type"
              :options="typeOptions"
              :label="t('e_connector.chargingStations.modal.field.type.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div v-if="type === ConnectionType.SOAP" class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.chargingStation.url')" optional class="basis-1/2" size="small">
            <InfuseInput
              size="small"
              v-model="url"
              type="url"
              :placeholder="t('e_connector.chargingStations.modal.field.url.placeholder')"
              :disabled="useRequestHeader"
            />
          </InfuseField>

          <InfuseField :label="t('e_connector.common.chargingStation.useRequestHeader')" class="basis-1/4" size="small">
            <InfuseToggle
              id="useRequestHeader"
              :value="useRequestHeader"
              :disabled="!!url"
              @on="useRequestHeader = true"
              @off="useRequestHeader = false"
            />
          </InfuseField>

          <InfuseField :label="t('e_connector.common.chargingStation.omitSoapHeader')" class="basis-1/4" size="small">
            <InfuseToggle
              id="omitSoapHeader"
              :value="omitSoapHeader"
              @on="omitSoapHeader = true"
              @off="omitSoapHeader = false"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStation.networkProfile')"
            :helpText="t('e_connector.chargingStations.modal.field.networkProfile.help')"
            class="basis-full"
            size="small"
          >
            <InfuseSelect
              name="networkProfile"
              size="small"
              v-model="networkProfileId"
              :options="networkProfileOptions"
              :label="t('e_connector.chargingStations.modal.field.networkProfile.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <h1 class="mt-1">Basic auth</h1>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStation.basicAuthStatus')"
            :helpText="basicAuthStatusHelpText"
            class="basis-1/2"
            size="small"
          >
            <InfuseSelect
              name="basicAuthStatus"
              size="small"
              v-model="basicAuthStatus"
              :options="basicAuthStatusOptions"
              :label="t('e_connector.chargingStations.modal.field.basicAuthStatus.placeholder')"
              :required="true"
            />
          </InfuseField>

          <InfuseField
            :label="t('e_connector.chargingStations.modal.field.basicAuthDeviation.label')"
            :helpText="t('e_connector.chargingStations.modal.field.basicAuthDeviation.help')"
            class="basis-1/2"
            size="small"
          >
            <InfuseSelect
              name="type"
              size="small"
              v-model="basicAuthDeviationId"
              :options="basicAuthDeviationOptions"
              :label="t('e_connector.chargingStations.modal.field.basicAuthDeviation.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.chargingStations.modal.field.shouldSetBasicAuthPassword.label')"
            class="basis-1/2"
            size="small"
          >
            <InfuseToggle
              id="shouldSetBasicAuthPassword"
              :value="shouldSetNewBasicAuthPassword"
              @on="shouldSetNewBasicAuthPassword = true"
              @off="shouldSetNewBasicAuthPassword = false"
            />
          </InfuseField>

          <InfuseField
            :label="t('e_connector.common.chargingStation.basicAuthPassword')"
            class="basis-1/2"
            :class="{ invisible: !shouldSetNewBasicAuthPassword }"
            size="small"
          >
            <template #label>
              <InfuseTooltip
                :content="t('e_connector.chargingStations.modal.field.shouldSetBasicAuthPassword.tooltip')"
                class="inline cursor-help text-yellow-500"
              >
                <font-awesome-icon :icon="['fal', 'circle-exclamation']" />
              </InfuseTooltip>
            </template>

            <InfuseInput
              size="small"
              v-model="basicAuthPassword"
              :maxlength="255"
              :placeholder="t('e_connector.chargingStations.modal.field.basicAuthPassword.placeholder')"
              :required="false"
            >
              <template #clear="{ value }">
                <InfuseCopy v-slot="scope" class="absolute">
                  <font-awesome-icon
                    class="w-5/6 cursor-pointer text-neutral-500 hover:text-neutral-600"
                    :icon="['far', 'copy']"
                    @click="scope.copy(value.toString())"
                  />
                </InfuseCopy>
              </template>
            </InfuseInput>
          </InfuseField>
        </div>
      </div>
    </form>

    <template #actions>
      <InfuseButton color="secondary" @click="emit('close')">
        {{ t('e_connector.common.action.cancel') }}
      </InfuseButton>
      <InfuseButton type="submit" form="create-charging-station" :disabled="loading" :loading="loading">
        {{ t('e_connector.common.action.create') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import {
  InfuseButton,
  InfuseCopy,
  InfuseField,
  InfuseInput,
  InfuseModal,
  InfuseSelect,
  InfuseToggle,
  InfuseTooltip
} from '@infuse/ui/components';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ConnectionType, OcppVersion } from '@infuse/e-connector/central-system/domain';
import { CreateChargingStation } from '@infuse/api/e-connector/charging-station';
import { BasicAuthStatus, ChargingStation } from '@infuse/e-connector/charging-station/domain';
import { NetworkProfile } from '@infuse/e-connector/network-profile/domain';
import { BasicAuthDeviation } from '@infuse/e-connector/basic-auth-deviation/domain';
import { generateBasicAuthPassword } from '@infuse/e-connector/charging-station/utils';

interface Props {
  loading: boolean;
  networkProfiles: NetworkProfile[];
  basicAuthDeviations: BasicAuthDeviation[];
}

interface Emits {
  (e: 'close'): void;
  (e: 'create', createChargingStation: CreateChargingStation): void;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});

const emit = defineEmits<Emits>();

const { t } = useI18n();

const name = ref<string>('');
const type = ref<ConnectionType>(ConnectionType.WEBSOCKET);
const ocppVersion = ref<OcppVersion>(OcppVersion.V16);
const networkProfileId = ref<number>(1);
const basicAuthStatus = ref<BasicAuthStatus>(BasicAuthStatus.NOT_REQUIRED);

const basicAuthDeviationId = ref<number>(-1);
const shouldSetNewBasicAuthPassword = ref<boolean>(false);
const basicAuthPassword = ref<string>('');

const url = ref<string>('');
const useRequestHeader = ref<boolean>(false);
const omitSoapHeader = ref<boolean>(false);

const typeOptions = Object.values(ConnectionType);
const ocppVersionOptions = Object.values(OcppVersion);
const networkProfileOptions = computed(() =>
  props.networkProfiles.map((networkProfile) => ({
    key: networkProfile.name,
    value: networkProfile.id
  }))
);
const basicAuthDeviationOptions = computed(() => [
  {
    key: t('e_connector.chargingStations.modal.field.basicAuthDeviation.default'),
    value: -1
  },
  ...props.basicAuthDeviations.map((d) => ({
    key: d.label,
    value: d.id
  }))
]);
const basicAuthStatusOptions = Object.values(BasicAuthStatus);

const basicAuthStatusHelpText = computed(() => {
  switch (basicAuthStatus.value) {
    case BasicAuthStatus.REQUIRED:
      return t('e_connector.chargingStations.modal.field.basicAuthStatus.help.required');
    case BasicAuthStatus.NOT_REQUIRED:
      return t('e_connector.chargingStations.modal.field.basicAuthStatus.help.notRequired');
    case BasicAuthStatus.ENABLE_NEXT_CONNECT:
      return t('e_connector.chargingStations.modal.field.basicAuthStatus.help.enableNextConnect');
    case BasicAuthStatus.CONFIGURE_NEXT_CONNECT:
      return t('e_connector.chargingStations.modal.field.basicAuthStatus.help.configureNextConnect');
    default:
      return '';
  }
});

watch(
  () => shouldSetNewBasicAuthPassword.value,
  (newValue) => {
    basicAuthPassword.value = newValue ? generateBasicAuthPassword() : '';
  }
);

watch(
  () => type.value,
  (newValue) => {
    if (newValue !== ConnectionType.SOAP) {
      url.value = '';
      useRequestHeader.value = false;
      omitSoapHeader.value = false;
    }
  }
);

function onSave() {
  const createChargingStation = {
    name: name.value,
    type: type.value as ConnectionType,
    ocppVersion: ocppVersion.value as OcppVersion,
    uri: url.value,
    useManualURI: false,
    useRequestHeader: useRequestHeader.value,
    omitSoapHeader: omitSoapHeader.value,
    basicAuthStatus: basicAuthStatus.value as BasicAuthStatus,
    basicAuthPassword: basicAuthPassword.value,
    basicAuthDeviationId: basicAuthDeviationId.value.toString(),
    networkProfileId: networkProfileId.value
  } as CreateChargingStation;

  if (!shouldSetNewBasicAuthPassword.value) {
    createChargingStation.basicAuthPassword = undefined;
  }

  emit('create', createChargingStation as ChargingStation);
}
</script>
